<template>
  <div class="titan-grid">
    <desktop :settings="settings"
      :loading="loading"
      :sortable="sortable"
      :conditions="conditions"
      :items="items"
      @onFilter="setConditions($event)"
      @buttonEvent="$emit('button-' + $event.event, $event.item)"
      v-if="mobile === false"
    >
      <template :slot="column.key" slot-scope="slotData" v-for="column in columns">
        <slot :name="column.key" :item="slotData.item">
          <column :item="slotData.item"
            :settings="column"
            :selected="selectedItems"
            @toggleSelected="toggleSelected($event)"
          />
        </slot>
      </template>
      <template slot="before-actions" slot-scope="slotData">
        <slot name="before-actions" :item="slotData.item"></slot>
      </template>
      <template slot="after-actions" slot-scope="slotData">
        <slot name="after-actions" :item="slotData.item"></slot>
      </template>
    </desktop>

    <mobile :settings="settings"
      :loading="loading"
      :sortable="sortable"
      :conditions="conditions"
      :items="items"
      @onFilter="setConditions($event)"
      @buttonEvent="$emit('button-' + $event.event, $event.item)"
      v-if="mobile === true"
    >
      <template :slot="column.key" slot-scope="slotData" v-for="column in columns">
        <slot :name="column.key" :item="slotData.item">
          <column :item="slotData.item"
            :settings="column"
            :selected="selectedItems"
            @toggleSelected="toggleSelected($event)"
          />
        </slot>
      </template>
      <template slot="before-actions" slot-scope="slotData">
        <slot name="before-actions" :item="slotData.item"></slot>
      </template>
      <template slot="after-actions" slot-scope="slotData">
        <slot name="after-actions" :item="slotData.item"></slot>
      </template>
    </mobile>
    <div class="titan-grid-footer">
      <slot name="before-paginator" :item="paginator"></slot>
      <paginator v-if="loading === false && hidePaginator === false"
        :pages="this.paginator.pages"
        :page="this.page"
        v-on:changePage="changePage($event)"
      />
      <slot name="after-paginator" :item="paginator"></slot>
    </div>
  </div>
</template>
<script>
  import Column from './Column'
  import Paginator from './Paginator'
  import AcceptAction from './../AcceptAction'
  import Buttons from '../Buttons'
  import GridFilter from './GridFilter'
  import Desktop from './tables/Desktop'
  import Mobile from './tables/Mobile'
  import { getSortObject, getConditionsObject, isNum } from './utils'
  import { get, has } from 'lodash'

  import { library } from '@fortawesome/fontawesome-svg-core'
  import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons'

  library.add(faSort, faSortUp, faSortDown)

  export default {
    components: {
      GridFilter,
      Buttons,
      Column,
      Paginator,
      AcceptAction,
      Desktop,
      Mobile,
    },
    props: {
      settings: {
        type: Object,
        required: true,
      },
      storeDispatchList: {
        type: String | Function,
        required: true,
      },
      storeDispachParams: {
        required: false,
      },
      page: {
        type: Number,
        'default': 1,
      },
      hidePaginator: {
        type: Boolean,
        default: false,
      },
      selectedItems: {
        type: Array,
        required: false,
        default: () => {
          return []
        },
      },
    },
    watch: {
      sortable: {
        handler: function () {
          this.changePage(1)
          this.sortSearchFilterAndSliceDataForPage()
          // this.fetchData(true);
        },
        deep: true,
      },
      '$props.page'() {
        this.sortSearchFilterAndSliceDataForPage()
        // this.fetchData();
      },
    },
    mounted() {
      this.initSortColumns()
      this.fetchData()
      this.$nextTick(() => {
        window.addEventListener('resize', this.setPageWidth)
        this.setPageWidth()
      })
    },
    computed: {
      mobile() {
        const breakPoint = this.settings.mobileBreakPoint === undefined ? 768 : this.settings.mobileBreakPoint
        return this.pageWidth < breakPoint
      },
      columns() {
        return this.settings.columns === undefined ? [] : this.settings.columns
      },
      queryParams() {
        return {
          page: this.page,
          conditions: getConditionsObject({ ...this.conditions }, this.settings.columns),
          sort: getSortObject(this.sortable, this.settings.columns),
          custom: this.storeDispachParams,
        }
      },
    },
    methods: {
      initSortColumns() {
        this.sortable.key = this.settings.defaultSortColumn === undefined ? 'id' : this.settings.defaultSortColumn
        this.sortable.direction = this.settings.defaultSortDirection === undefined ? 'DESC' : this.settings.defaultSortDirection
      },
      setConditions(conditions) {
        this.conditions = conditions
        if (this.page !== 1) {
          this.changePage(1)
        }
        // this.fetchData(false);
        this.sortSearchFilterAndSliceDataForPage()
      },
      changePage(page) {
        if (this.page !== page) {
          this.$emit('changePage', page)
          this.fetchData()
        }
      },
      processConditions(items) {
        const conditions = getConditionsObject({ ...this.conditions }, this.settings.columns)

        const operators = {
          '<': (a, b) => a < b,
          '<=': (a, b) => a <= b,
          '=': (a, b) => a === b,
          '>': (a, b) => a > b,
          '>=': (a, b) => a >= b,
        }

        conditions.forEach(condition => {
          items = items.filter(item => {
            switch (condition.type) {
              case 'fulltext':
                if (Array.isArray(condition.key)) {
                  let found = false
                  for (const key of condition.key) {
                    let keyParts = key.split('.*.')
                    if (1 === keyParts.length) {
                      found |= this.normalizeForSearch(get(item, key)).includes(this.normalizeForSearch(condition.value))
                    } else if (2 === keyParts.length) {
                      const [firstPartKey, secondPartKey] = keyParts
                      const firstPartItems = get(item, firstPartKey)
                      for (const partItem of firstPartItems) {
                        found |= this.normalizeForSearch(get(partItem, secondPartKey)).includes(this.normalizeForSearch(condition.value))
                      }
                    }
                  }

                  return found
                } else {
                  // not sure if needed ..
                }
                break

              case 'number':
                let value = condition.value
                if ('function' === typeof condition.postProcessFilterValue) {
                  value = condition.postProcessFilterValue(value)
                }
                return get(item, condition.key) === value
                break

              case 'dateRange':
                return operators[condition.operator](get(item, condition.key), condition.value)
                break

              case 'select':
                return get(item, condition.key) === condition.value
                break
            }
          })
        })

        return items
      },
      sortSearchFilterAndSliceDataForPage() {
        let items = this.allItems.slice()

        items = this.processConditions(items)

        const sort = getSortObject(this.sortable, this.settings.columns)

        items.sort(function (a, b) {
          const sortCopy = JSON.parse(JSON.stringify(sort))

          let sortKeys = Object.keys(sortCopy)
          const sortKeysLength = Object.keys(sortCopy).length
          if (0 === sortKeysLength) {
            return
          }

          for (const key in sortCopy) {
            if (sortCopy.hasOwnProperty(key)) {
              sortCopy[key] = 'desc' === sortCopy[key].toLowerCase() ? -1 : 1
            }
          }

          let sorted = 0
          let i = 0

          while (0 === sorted && i < sortKeysLength) {
            const key = sortKeys[i]
            i++
            const keyDir = sortCopy[key]
            if (has(a, key) && has(b, key)) {
              const aValue = get(a, key);
              const bValue = get(b, key);

              if (aValue === bValue) {
                sorted = 0
                continue
              }

              if (isNum(aValue) && isNum(bValue)) {
                sorted = aValue > bValue ? 1 * keyDir : -1 * keyDir
                continue
              }

              sorted = String(aValue).localeCompare(String(bValue)) * keyDir
            }
          }

          return sorted
        })

        this.items = items.slice((this.page - 1) * 10, this.page * 10)
      },
      normalizeForSearch(value) {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      },
      fetchData(loading = true) {
        this.loading = loading
        this.updateSelected()

        const promise = 'function' === typeof this.storeDispatchList
          ? this.storeDispatchList(this.queryParams)
          : this.$store.dispatch(this.storeDispatchList, this.queryParams)

        promise.then(response => {
          this.paginator = {
            items: response.count,
            page: this.page,
            pages: Math.ceil(response.count / 10),
            perPage: 10,
          }
          this.allItems = response.items
          this.sortSearchFilterAndSliceDataForPage()
          this.loading = false
        }).catch(() => {
          this.loading = false
          // todo:
        })
      },
      setPageWidth() {
        this.pageWidth = document.documentElement.clientWidth
      },
      toggleSelected(id) {
        const selectedItems = [...this.selectedItems]
        const found = selectedItems.findIndex(item => id === item)
        if (-1 === found) {
          selectedItems.push(id)
        } else {
          selectedItems.splice(found, 1)
        }
        this.updateSelected(selectedItems)
      },
      updateSelected(list = []) {
        this.$emit('selectedItems', list)
      },
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.setPageWidth)
    },
    data() {
      return {
        loading: true,
        paginator: {},
        items: [],
        allItems: [],
        conditions: {},
        sortable: {
          key: 'id',
          direction: 'DESC',
        },
        pageWidth: 0,
      }
    },
  }
</script>
<style lang="scss">
  .titan-grid {
    border-radius: 5px;

    .titan-grid-footer {
      display: flex;
      flex-direction: row;
      margin-bottom: 30px;

      .paginator {
        margin-bottom: 0;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/core/InlineDataGrid/DataGrid.vue